import { Link, useLocation } from "react-router-dom";
import { useAuthStore } from "../../store/useAuthStore";
import { useMemo, useState } from "react";
import { Icon } from "@iconify/react";

interface NavItem {
  to: string;
  label: string;
  iconSrc: string;
  dividerBefore?: boolean;
}

const adminItems: NavItem[] = [
  { to: "/dashboard", label: "DASHBOARD", iconSrc: "/sidenav/dashboard.svg" },
  { to: "/locations", label: "LOCATIONS", iconSrc: "/sidenav/location.svg" },
  { to: "/devices", label: "DEVICES", iconSrc: "/sidenav/devices.svg" },
  { to: "/compliance", label: "COMPLIANCE", iconSrc: "/sidenav/reports.svg", dividerBefore: true },
  { to: "/reports", label: "REPORTS", iconSrc: "/sidenav/complaince.svg" },
  { to: "/alerts", label: "ALERTS", iconSrc: "/sidenav/alerts.svg" },
  // { to: "/email/template", label: "EMAIL TEMPLATES", iconSrc: "/sidenav/email.svg", dividerBefore: true },
  { to: "/settings", label: "SETTINGS", iconSrc: "/sidenav/settings.svg", dividerBefore: true },
];

const userItems: NavItem[] = [
  { to: "/dashboard", label: "DASHBOARD", iconSrc: "/sidenav/dashboard.svg" },
  { to: "/locations", label: "LOCATIONS", iconSrc: "/sidenav/location.svg" },
  { to: "/devices", label: "DEVICES", iconSrc: "/sidenav/devices.svg" },
  { to: "/compliance", label: "COMPLIANCE", iconSrc: "/sidenav/reports.svg", dividerBefore: true },
  { to: "/reports", label: "REPORTS", iconSrc: "/sidenav/complaince.svg" },
  { to: "/alerts", label: "ALERTS", iconSrc: "/sidenav/alerts.svg" },
  { to: "/settings", label: "SETTINGS", iconSrc: "/sidenav/settings.svg", dividerBefore: true },
];

function getItems(role: string): NavItem[] {
  const r = role?.toUpperCase();
  if (r === "ADMIN" || r === "SUPER_ADMIN") return adminItems;
  return userItems;
}

interface SidebarProps {
  onToggle?: (expanded: boolean) => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  mobileFooter?: React.ReactNode;
}

export function Sidebar({ onToggle, mobileOpen, onMobileClose, mobileFooter }: SidebarProps) {
  const location = useLocation();
  const { role } = useAuthStore();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onToggle?.(newExpanded);
  };
  const items = useMemo(() => getItems(role ?? "user"), [role]);

  const renderSidebarContent = (isMobile: boolean) => (
    <>
      {/* Logo Section */}
      <div className={`flex items-center h-16 ${isMobile || isExpanded ? "pl-7 pr-4" : "justify-center"}`}>
        <Link to="/dashboard" onClick={isMobile ? onMobileClose : undefined} className="block">
          <div className={`flex items-center transition-all duration-300`}>
            <img
              src="/OS_ICON.png"
              alt="Logo"
              className={`block transition-all duration-300 h-8 w-8`}
            />
            {(isMobile || isExpanded) && (
              <span className="ml-3 text-3xl font-bold uppercase tracking-wide font-['Open_Sans'] text-gray-900 dark:text-white transition-all duration-300">
                OSMOAIR
              </span>
            )}
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <div className={`flex-1 ${isMobile || isExpanded ? "overflow-y-auto scrollbar-hide" : ""}`}>
        <nav className={`space-y-1 py-4 ${isMobile || isExpanded ? "px-0 " : "px-0"}`}>
          {items.map((item) => {
            const active =
              location.pathname === item.to || location.pathname.startsWith(item.to + "/");
            const showExpanded = isMobile || isExpanded;
            const enlargedIcon = item.to === "/settings";
            const extraLargeIcon = ["/dashboard", "/locations"].includes(item.to);
            const deviceIcon = item.to === "/devices";
            const complianceIcon = item.to === "/compliance";

            return (
              <div key={item.to}>
                {item.dividerBefore && (
                  <div className="mx-5 my-1 border-t border-gray-300 dark:border-gray-600" />
                )}
                <div className="relative group">
                  <Link
                    to={item.to}
                    onClick={isMobile ? onMobileClose : undefined}
                    className={`
                      relative flex items-center text-sm font-semibold transition-all duration-200 group w-full
                      ${showExpanded ? "pl-7 pr-4 py-4" : "justify-center py-4"}
                      ${active ? "before:absolute before:right-0 before:top-1/2 before:-translate-y-1/2 before:h-9 before:w-1.5 before:bg-[#005FDF] before:rounded-" : ""}
                      ${
                        active
                          ? "bg-transparent text-gray-700 dark:text-gray-300"
                          : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300"
                      }
                    `}
                  >
                    <div className={`relative flex items-center ${showExpanded ? "w-full -ml-1" : "justify-center"}`}>
                      <div className="relative w-9 h-9 flex-shrink-0 flex items-center justify-center">
                        <img
                          src={item.iconSrc}
                          alt={item.label}
                          className={`${deviceIcon ? "w-[34px] h-[34px]" : extraLargeIcon ? "w-[32px] h-[32px]" : enlargedIcon ? "w-[30px] h-[30px]" : complianceIcon ? "w-[29px] h-[29px]" : "w-7 h-7"} transition-all duration-200 ${
                            active
                              ? "opacity-100 dark:invert"
                              : "opacity-60 dark:invert group-hover:opacity-100"
                          }`}
                        />
                      </div>
                      {showExpanded && (
                        <span className="ml-4 transition-opacity duration-200 font-inter">
                          {item.label}
                        </span>
                      )}
                    </div>
                  </Link>
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Footer with Toggle - Desktop only */}
      {!isMobile && (
        <div className="relative group pb-4">
          <button
            onClick={handleToggle}
            className={`
              relative flex items-center text-sm font-semibold transition-all duration-200 group w-full
              ${isExpanded ? "pl-7 pr-4 py-4" : "justify-center py-4"}
              text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300
            `}
            aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            <div className={`relative flex items-center ${isExpanded ? "w-full -ml-1" : "justify-center"}`}>
              <div className="relative w-9 h-9 flex-shrink-0 flex items-center justify-center">
                <Icon
                  icon="fluent:arrow-next-12-regular"
                  className={`w-7 h-7 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
                />
              </div>
              {isExpanded && (
                <span className="ml-4 transition-opacity duration-200 font-inter">COLLAPSE</span>
              )}
            </div>
          </button>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-40 md:hidden animate-in fade-in-0 duration-300"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen z-50 w-72 bg-white dark:bg-gray-900 border-r border-gray-300 dark:border-gray-700 shadow-lg transform transition-transform duration-300 ease-in-out md:hidden ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {renderSidebarContent(true)}
          {mobileFooter && (
            <div className="border-t border-gray-300 dark:border-gray-600 px-4 py-4">
              {mobileFooter}
            </div>
          )}
        </div>
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex md:flex-col fixed left-0 top-0 h-screen z-40 transition-all duration-300 ease-in-out ${
          isExpanded ? "md:w-72" : "md:w-20"
        } bg-white dark:bg-gray-900 border-r border-gray-300 dark:border-gray-700`}
      >
        {renderSidebarContent(false)}
      </aside>
    </>
  );
}
